import { Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ModalService } from '@app/shared/modals/services/modal.service';
import { SubSink } from 'subsink';
import { IMultiSelectOptionsModalInput, IMultiSelectOptionsModalOuput } from '@app/shared/modals/model/action-modal';
import {
  MultiSelectOptionsModalComponent
} from '@app/shared/modals/multi-select-options-modal/multi-select-options-modal.component';
import { GroupedOption } from '@app/run-planning/model/option';
import { MultiSelectFieldValidator } from '@app/shared/forms/multi-select/multi-select.validator';

export interface IMultiSelectFieldSettings {
  maxSelection?: number;
  minSelection?: number;
}

@Component({
  selector: 'app-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => MultiSelectComponent),
    },
  ],
})
export class MultiSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {

  private subs = new SubSink();

  @Input() valueProperty = 'value';
  @Input() labelProperty = 'label';
  @Input() groupedOptions: GroupedOption[] = [];
  @Input() settings: IMultiSelectFieldSettings = {};
  @Input() isRequired = false;
  @Output() addNewOptionEmitter = new EventEmitter<boolean>();

  selectMessage = 'Select items';
  selectMoreMessage = 'Select more items';
  selectionLimitErrorMessage: string = undefined;

  values: string[] = [];

  get selectedOptions() {
    return this.groupedOptions
      .reduce((acc, curr) => ([...acc, ...curr.options]), [])
      .filter(opt => this.values.includes(opt.value));
  }

  disabled = false;
  touched = false;

  onChange = (values) => {};

  onTouched = () => {};

  constructor(private modalService: ModalService) { }

  ngOnInit() {
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  writeValue(values: any): void {
    if (typeof values === 'string') {
      // Temporary patch against GSS malformed response
      // "[\n  \"grf.7ce6293481fd4b009e012c4912378cfa\",\n  \"grf.6ca71fa581864b9da7170ffd6624217e\",\n  \"grf.4af8e85418164ee89400236c9c6fccb7\"\n]"
      try {
        values = values.trim().slice(1, -1).replace(/\n/g, '').trim().split(',').map(item => item.trim().replace(/"/g, ''));
      } catch (error) {
        console.warn('Failed to parse values string to array.');
      }
    }
    this.values = values || [];
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  markAsTouched() {
    this.onTouched();
    if (!this.touched) {
      this.touched = true;
    }
  }

  updateValues(values: string[]) {
    this.markAsTouched();
    this.setErrorMessage(values);
    // Avoid passing the same reference to the values array to avoid unintended side effects of mutation.
    this.values = [...values];
    this.onChange([...values]);
  }

  private setErrorMessage(values: string[]) {
    this.selectionLimitErrorMessage = MultiSelectFieldValidator.withContext({
      isRequired: this.isRequired,
      minSelection: this.settings.minSelection,
      maxSelection: this.settings.maxSelection,
    }).validate(values);
  }

  handleSelectMore() {
    const modalInput: IMultiSelectOptionsModalInput = {
      selectedValues: [...this.values],
      groupedOptions: this.groupedOptions,
      valueProperty: this.valueProperty,
      labelProperty: this.labelProperty,
      settings: this.settings,
      isRequired: this.isRequired,
    };
    this.subs.sink = this.modalService.openModal(modalInput, MultiSelectOptionsModalComponent).confirm.subscribe({
      next: (output: IMultiSelectOptionsModalOuput) => {
        if (output.addNewOption) {
          this.addNewOptionEmitter.emit(true);
        }
        if (output.selectedValues) {
          this.updateValues(output.selectedValues);
        }
      },
      error: console.error,
    });
  }

  handleUnselectOption(valueToUnselect: string) {
    const updatedValues = this.values.filter(value => value !== valueToUnselect);
    this.updateValues(updatedValues);
  }

}
